import { CKBox, CKButton, CKLayoutGridAuto, CKTextFieldNext } from 'clearkit';
import { useState } from 'react';

import { AuthLayoutHeader } from '~/components/layouts/AuthLayout';

import useCreateAccountAndSignIn from '../../shared/useCreateAccountAndSignIn';
import { CreateAccountInput } from '../types';

type CreateAccountProps = {
  proposedOrg: CreateAccountInput;
};

type CreateAccountValidationErrors = {
  account_name?: string;
  user_first_name?: string;
  user_last_name?: string;
};

export default function CreateAccount({ proposedOrg }: CreateAccountProps) {
  const [userFirstName, setUserFirstName] = useState(
    proposedOrg.user_first_name || '',
  );
  const [userLastName, setUserLastName] = useState(
    proposedOrg.user_last_name || '',
  );
  const [accountName, setAccountName] = useState(
    proposedOrg.organization_name || '',
  );
  const [validationErrors, setValidationErrors] = useState<
    CreateAccountValidationErrors
  >({});

  const { isLoading, mutateAsync } = useCreateAccountAndSignIn();

  async function createAccount() {
    const { isSuccess, error } = await mutateAsync({
      accountName,
      userFirstName,
      userLastName,
    });

    if (isSuccess) {
      setValidationErrors({});
      window.location.href = '/';
      return;
    }

    if (error.action === 'unauthorized') {
      window.location.href = `/auth/login?error=${error.type}`;
      return;
    }

    if (error.error?.type === 'validation') {
      setValidationErrors(error.error.errors);
    }
  }

  return (
    <>
      <AuthLayoutHeader
        className="sm:w-80"
        description="Provide your details below and we'll create your account."
        header={
          <div className="w-full font-semibold">
            Create your Clearbit account
          </div>
        }
      />
      <CKBox className="w-full p-5 sm:w-[440px]">
        <div className="flex flex-col gap-6">
          <CKLayoutGridAuto gap="1.5rem">
            <CKTextFieldNext
              onChange={setUserFirstName}
              placeholder="First name"
              value={userFirstName}
              variant={validationErrors.user_first_name ? 'error' : undefined}
            >
              {validationErrors.user_first_name ? (
                <CKTextFieldNext.ErrorMessage>
                  {validationErrors.user_first_name}
                </CKTextFieldNext.ErrorMessage>
              ) : null}
            </CKTextFieldNext>
            <CKTextFieldNext
              onChange={setUserLastName}
              placeholder="Last name"
              value={userLastName}
              variant={validationErrors.user_last_name ? 'error' : undefined}
            >
              {validationErrors.user_last_name ? (
                <CKTextFieldNext.ErrorMessage>
                  {validationErrors.user_last_name}
                </CKTextFieldNext.ErrorMessage>
              ) : null}
            </CKTextFieldNext>
          </CKLayoutGridAuto>
          <CKTextFieldNext
            onChange={setAccountName}
            placeholder="Company name"
            value={accountName}
            variant={validationErrors.account_name ? 'error' : undefined}
          >
            {validationErrors.account_name ? (
              <CKTextFieldNext.ErrorMessage>
                {validationErrors.account_name}
              </CKTextFieldNext.ErrorMessage>
            ) : null}
          </CKTextFieldNext>
          <div>
            <CKButton
              isLoading={isLoading}
              onClick={createAccount}
              variant="bold"
              variantColor="blue"
            >
              Create account
            </CKButton>
          </div>
        </div>
      </CKBox>
    </>
  );
}
